import React, { useEffect, useRef, useState } from "react";
import { Download, X } from "lucide-react";
import { api } from "../../lib/api";

function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function addDays(date, count) {
  const result = new Date(date);
  result.setDate(result.getDate() + count);
  return result;
}

export default function EGScheduleExport({ period, weekStart, range, onRangeChange, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const request = useRef(null);
  const firstInput = useRef(null);
  useEffect(() => { firstInput.current?.focus(); return () => request.current?.abort(); }, []);
  const valid = range.start && range.end && range.start <= range.end && (Date.parse(range.end) - Date.parse(range.start)) / 86400000 < 93;
  const changeRange = value => { setError(""); setSaved(false); onRangeChange(value); };

  async function download() {
    if (!valid || busy) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setError(""); setSaved(false);
    try {
      const response = await api.get(`/scheduling/periods/${encodeURIComponent(period.id)}/eg-export.pdf`, {
        params: { start: range.start, end: range.end }, responseType: "blob", signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!String(response.headers?.["content-type"] || response.data?.type || "").includes("application/pdf")) {
        throw new Error("The schedule could not be exported as a PDF. Please try again.");
      }
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `TOTO-EG-${period.boutique_code}-${range.start}-to-${range.end}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      setSaved(true);
    } catch (problem) {
      if (controller.signal.aborted) return;
      let detail;
      try { detail = JSON.parse(await problem.response.data.text()).detail; } catch {}
      if (controller.signal.aborted) return;
      setError(typeof detail === "string" ? detail : problem.response?.status === 401 ? "Sign in again to download the saved schedule." : problem.message || "The PDF could not be downloaded. Your schedule is unchanged.");
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  return <section className="eg-export-options" aria-labelledby="eg-export-heading">
    <header><div><h2 id="eg-export-heading">Export schedule</h2><p>{period.label} · {period.boutique_code}</p></div><button type="button" onClick={onClose} aria-label="Close export options"><X size={20}/></button></header>
    <p>Choose any date range, including dates from another month (up to 93 days).</p>
    <div className="eg-export-presets"><button type="button" disabled={busy} onClick={() => changeRange({ start: period.period_start, end: period.period_end })}>Entire month</button><button type="button" disabled={busy} onClick={() => changeRange({ start: isoDate(weekStart), end: isoDate(addDays(weekStart, 6)) })}>Displayed week</button><button type="button" disabled={busy} onClick={() => changeRange({ start: [isoDate(weekStart), period.period_start].sort()[0], end: [isoDate(addDays(weekStart, 6)), period.period_end].sort().pop() })}>Displayed week + month</button></div>
    <div className="eg-export-dates"><label>Start date<input ref={firstInput} type="date" value={range.start} disabled={busy} onChange={event => changeRange({ ...range, start: event.target.value })}/></label><label>End date<input type="date" value={range.end} disabled={busy} onChange={event => changeRange({ ...range, end: event.target.value })}/></label></div>
    {!valid && <p role="alert">Choose an end date on or after the start date, within 93 days.</p>}
    <p className="eg-export-note">The PDF includes saved schedules for the selected dates and their review status. You can print the downloaded PDF.</p>
    {error && <p role="alert">{error}</p>}
    {saved && <p role="status">PDF download started. Check your Downloads folder before attaching it.</p>}
    <footer><button type="button" disabled={!valid || busy} onClick={download}><Download size={18}/>{busy ? "Preparing PDF…" : "Download PDF"}</button></footer>
  </section>;
}
